import React, { useState } from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Card as RawCard} from "@/components/ui/card";
import { CheckCircle2, Download, Calendar, Clock, Users, CircleDot, Ship, Car, Building2, Moon, MapPin, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";

const Button = /** @type {React.ComponentType<any>} */ (RawButton);
const Card = /** @type {React.ComponentType<any>} */ (RawCard);

/**
 * @param {Object} props
 * @param {{ golf: any; ferry: any; transport: any; accommodation: any }} props.bookings
 * @param {() => void} props.onBackToSearch
 * @param {(totalAmount: number) => Promise<void>} [props.onPay]
 */
export default function UnifiedConfirmationView({ bookings, onBackToSearch, onPay }) {
  const { golf, ferry, transport, accommodation } = bookings;
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [completedBookings, setCompletedBookings] = useState({ golf: null, ferry: null, transport: null, accommodation: null });

  const totalAmount =
    (golf?.totalAmount ?? 0) +
    (ferry?.totalAmount ?? 0) +
    (transport?.totalAmount ?? 0) +
    (accommodation?.totalAmount ?? 0);

  const handleDownloadItinerary = () => {
    const { golf, ferry, transport, accommodation } = completedBookings;
    const sections = [];

    if (golf) {
      sections.push(`
        <div class="section">
          <h2>Golf Booking</h2>
          <p class="title">${golf.course?.name ?? ''}</p>
          <table>
            <tr><td>Date</td><td>${golf.date ? format(new Date(golf.date), "dd MMM yyyy") : ''}</td></tr>
            <tr><td>Tee Time</td><td>${golf.teeTime ?? ''}</td></tr>
            <tr><td>Players</td><td>${golf.players ?? ''}</td></tr>
            <tr><td>Amount</td><td>SGD ${golf.totalAmount?.toFixed(2) ?? '0.00'}</td></tr>
          </table>
        </div>`);
    }

    if (ferry) {
      sections.push(`
        <div class="section">
          <h2>Ferry Booking</h2>
          <p class="title">${ferry.ferry?.operator ?? ''}</p>
          <p class="subtitle">${ferry.ferry?.origin_port ?? ''} &rarr; ${ferry.ferry?.destination_port ?? ''}</p>
          <table>
            <tr><td>Date</td><td>${ferry.date ? format(new Date(ferry.date), "dd MMM yyyy") : ''}</td></tr>
            <tr><td>Departure</td><td>${ferry.departureTime ?? ''}</td></tr>
            <tr><td>Passengers</td><td>${ferry.passengers ?? ''}</td></tr>
            <tr><td>Amount</td><td>SGD ${ferry.totalAmount?.toFixed(2) ?? '0.00'}</td></tr>
          </table>
        </div>`);
    }

    if (transport) {
      sections.push(`
        <div class="section">
          <h2>Land Transport</h2>
          <p class="title">${transport.vehicle?.provider ?? ''}</p>
          <p class="subtitle">${transport.vehicle?.pickup_location ?? ''} &rarr; ${transport.vehicle?.dropoff_location ?? ''}</p>
          <table>
            <tr><td>Date</td><td>${transport.date ? format(new Date(transport.date), "dd MMM yyyy") : ''}</td></tr>
            <tr><td>Pickup Time</td><td>${transport.pickupTime ?? ''}</td></tr>
            <tr><td>Passengers</td><td>${transport.passengers ?? ''}</td></tr>
            <tr><td>Amount</td><td>SGD ${transport.totalAmount?.toFixed(2) ?? '0.00'}</td></tr>
            ${transport.isRoundTrip && transport.returnDate ? `<tr><td>Return</td><td>${format(new Date(transport.returnDate), "dd MMM yyyy")} at ${transport.returnPickupTime ?? ''}</td></tr>` : ''}
          </table>
        </div>`);
    }

    if (accommodation) {
      sections.push(`
        <div class="section">
          <h2>Accommodation</h2>
          <p class="title">${accommodation.hotel?.name ?? ''}</p>
          <p class="subtitle">${accommodation.hotel?.area ?? ''}</p>
          <table>
            <tr><td>Check-in</td><td>${accommodation.checkin ? format(new Date(accommodation.checkin), "dd MMM yyyy") : ''}</td></tr>
            <tr><td>Check-out</td><td>${accommodation.checkout ? format(new Date(accommodation.checkout), "dd MMM yyyy") : ''}</td></tr>
            <tr><td>Nights</td><td>${accommodation.nights ?? ''}</td></tr>
            <tr><td>Amount</td><td>SGD ${accommodation.totalAmount?.toFixed(2) ?? '0.00'}</td></tr>
          </table>
        </div>`);
    }

    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Exqura Itinerary</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #0B2421; margin: 0; padding: 40px; background: #fff; }
  .header { text-align: center; margin-bottom: 32px; }
  .header .logo { font-size: 22px; font-weight: 700; }
  .header .logo span { color: #D4AF37; }
  h1 { font-size: 24px; margin: 12px 0 4px; }
  .subtext { color: #6b7280; margin: 0 0 24px; }
  .section { border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px 24px; margin-bottom: 16px; border-left: 4px solid #D4AF37; }
  .section h2 { font-size: 16px; margin: 0 0 8px; }
  .section .title { font-weight: 600; margin: 0 0 4px; }
  .section .subtitle { color: #6b7280; font-size: 14px; margin: 0 0 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 14px; }
  table td { padding: 4px 0; color: #374151; }
  table td:first-child { color: #6b7280; width: 40%; }
  .total { display: flex; justify-content: space-between; align-items: center; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px 24px; margin-top: 24px; }
  .total h3 { margin: 0; font-size: 18px; }
  .total .amount { font-size: 24px; font-weight: 700; color: #D4AF37; }
  .footer { margin-top: 32px; text-align: center; color: #9ca3af; font-size: 12px; }
</style>
</head>
<body>
  <div class="header">
    <div class="logo">EXQURA <span>GOLF &amp; EXPERIENCES</span></div>
    <h1>Trip Itinerary</h1>
    <p class="subtext">Booking confirmed on ${format(new Date(), "dd MMM yyyy 'at' h:mm a")}</p>
  </div>
  ${sections.join('\n')}
  <div class="total">
    <h3>Total Amount Paid</h3>
    <div class="amount">SGD ${totalAmount.toFixed(2)}</div>
  </div>
  <div class="footer">Thank you for booking with Exqura. A confirmation email has been sent to your inbox.</div>
</body>
</html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Exqura-Itinerary-${format(new Date(), "yyyy-MM-dd")}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    try {
      setCompletedBookings({ golf, ferry, transport, accommodation });
      setIsCompleted(true);
    } catch (error) {
      console.error("Payment error:", error);
      alert(`Payment failed: ${error.message || 'Unknown error'}. Please try again.`);
    } finally {
      setIsProcessing(false);
    }
  };

  if (isCompleted) {
    return (
      <div className="min-h-screen bg-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Success Header */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <div className="w-20 h-20 bg-[#0B2421] rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-12 w-12 text-[#D4AF37]" />
            </div>
            <h1 className="text-4xl font-bold text-[#0B2421] mb-3">Payment Successful!</h1>
            <p className="text-xl text-gray-600">Your complete trip is now confirmed</p>
          </motion.div>

          {/* Bookings Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-4 mb-8"
          >
            {completedBookings.golf && (
              <Card className="p-6 border-l-4 border-l-[#D4AF37] bg-white border border-gray-100 rounded-2xl shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                    <CircleDot className="h-6 w-6 text-[#D4AF37]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-bold text-[#0B2421]">Golf Booking</h3>
                      <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800 rounded-full">Confirmed</span>
                    </div>
                    <p className="text-[#0B2421] font-semibold mb-2">{completedBookings.golf?.course?.name}</p>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {completedBookings.golf?.date ? format(new Date(completedBookings.golf.date), "dd MMM yyyy") : ''}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {completedBookings.golf?.teeTime}
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {completedBookings.golf?.players} players
                      </div>
                      <div className="font-semibold text-[#D4AF37]">
                        SGD {completedBookings.golf?.totalAmount?.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {completedBookings.ferry && (
              <Card className="p-6 border-l-4 border-l-[#D4AF37] bg-white border border-gray-100 rounded-2xl shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Ship className="h-6 w-6 text-[#D4AF37]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-bold text-[#0B2421]">Ferry Booking</h3>
                      <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800 rounded-full">Confirmed</span>
                    </div>
                    <p className="text-[#0B2421] font-semibold mb-2">{completedBookings.ferry.ferry.operator}</p>
                    <p className="text-sm text-gray-600 mb-2">
                      {completedBookings.ferry.ferry.origin_port} → {completedBookings.ferry.ferry.destination_port}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(completedBookings.ferry.date), "dd MMM yyyy")}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {completedBookings.ferry.departureTime}
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {completedBookings.ferry.passengers} passengers
                      </div>
                      <div className="font-semibold text-[#D4AF37]">
                        SGD {completedBookings.ferry.totalAmount.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {completedBookings.transport && (
              <Card className="p-6 border-l-4 border-l-[#D4AF37] bg-white border border-gray-100 rounded-2xl shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Car className="h-6 w-6 text-[#D4AF37]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-bold text-[#0B2421]">Land Transport</h3>
                      <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800 rounded-full">Confirmed</span>
                    </div>
                    <p className="text-[#0B2421] font-semibold mb-2">{completedBookings.transport.vehicle?.provider}</p>
                    <p className="text-sm text-gray-600 mb-2">
                      {completedBookings.transport.vehicle?.pickup_location} → {completedBookings.transport.vehicle?.dropoff_location}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(completedBookings.transport.date), "dd MMM yyyy")}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {completedBookings.transport.pickupTime}
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {completedBookings.transport.passengers} passengers
                      </div>
                      <div className="font-semibold text-[#D4AF37]">
                        SGD {completedBookings.transport.totalAmount.toFixed(2)}
                      </div>
                    </div>
                    {completedBookings.transport.isRoundTrip && completedBookings.transport.returnDate && (
                      <div className="mt-2 pt-2 border-t border-gray-100 text-sm text-gray-600">
                        <span className="font-medium text-[#0B2421]">Return: </span>
                        {format(new Date(completedBookings.transport.returnDate), "dd MMM yyyy")} at {completedBookings.transport.returnPickupTime}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {completedBookings.accommodation && (
              <Card className="p-6 border-l-4 border-l-[#D4AF37] bg-white border border-gray-100 rounded-2xl shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-6 w-6 text-[#D4AF37]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-bold text-[#0B2421]">Accommodation</h3>
                      <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800 rounded-full">Confirmed</span>
                    </div>
                    <p className="text-[#0B2421] font-semibold mb-1">{completedBookings.accommodation.hotel?.name}</p>
                    <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {completedBookings.accommodation.hotel?.area}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(completedBookings.accommodation.checkin), "dd MMM yyyy")}
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(completedBookings.accommodation.checkout), "dd MMM yyyy")}
                      </div>
                      <div className="flex items-center gap-2">
                        <Moon className="h-4 w-4" />
                        {completedBookings.accommodation.nights} nights
                      </div>
                      <div className="font-semibold text-[#D4AF37]">
                        SGD {completedBookings.accommodation.totalAmount.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </motion.div>

          {/* Total */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-xl font-bold text-[#0B2421]">Total Amount Paid</h3>
                <div className="text-3xl font-bold text-[#D4AF37]">
                  SGD {totalAmount.toFixed(2)}
                </div>
              </div>
              <p className="text-sm text-gray-500">
                All bookings confirmed. A confirmation email has been sent to your inbox.
              </p>
            </Card>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-8 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              variant="outline"
              className="w-full sm:w-auto h-12 gap-2 border-gray-200 text-[#0B2421] hover:bg-gray-50"
              onClick={handleDownloadItinerary}
            >
              <Download className="h-4 w-4" />
              Download Itinerary
            </Button>
            <Button
              onClick={onBackToSearch}
              className="w-full sm:w-auto h-12 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold"
            >
              Book Another Trip
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Button
            variant="ghost"
            onClick={onBackToSearch}
            className="gap-2 -ml-2 text-[#0B2421] hover:bg-transparent hover:text-[#0B2421]/70 hover:underline mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Booking Selection
          </Button>
          <h1 className="text-3xl font-bold text-[#0B2421] mb-2 text-center">Complete Your Booking</h1>
          <p className="text-gray-500 mb-8 text-center">Review and pay for all selected services</p>
        </motion.div>

        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="p-8 bg-white border border-gray-100 rounded-2xl shadow-sm">
              <h2 className="text-xl font-bold text-[#0B2421] mb-6">Selected Services</h2>

              <div className="space-y-3 mb-6">
                {golf && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 border border-gray-100 rounded-xl">
                    <div className="flex items-center gap-2">
                      <CircleDot className="h-5 w-5 text-[#0B2421]" />
                      <span className="font-medium text-[#0B2421]">Golf at {golf.course.name}</span>
                    </div>
                    <span className="font-bold text-[#D4AF37]">SGD {golf.totalAmount.toFixed(2)}</span>
                  </div>
                )}
                {ferry && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 border border-gray-100 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Ship className="h-5 w-5 text-[#0B2421]" />
                      <span className="font-medium text-[#0B2421]">Ferry by {ferry.ferry.operator}</span>
                    </div>
                    <span className="font-bold text-[#D4AF37]">SGD {ferry.totalAmount.toFixed(2)}</span>
                  </div>
                )}
                {transport && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 border border-gray-100 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Car className="h-5 w-5 text-[#0B2421]" />
                      <span className="font-medium text-[#0B2421]">Transport by {transport.vehicle?.provider}</span>
                    </div>
                    <span className="font-bold text-[#D4AF37]">SGD {transport.totalAmount.toFixed(2)}</span>
                  </div>
                )}
                {accommodation && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 border border-gray-100 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-[#0B2421]" />
                      <span className="font-medium text-[#0B2421]">{accommodation.hotel?.name}</span>
                    </div>
                    <span className="font-bold text-[#D4AF37]">SGD {accommodation.totalAmount.toFixed(2)}</span>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-100 pt-4 space-y-2 mb-6">
                {golf && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Golf</span>
                    <span className="font-medium text-[#0B2421]">SGD {golf.totalAmount.toFixed(2)}</span>
                  </div>
                )}
                {ferry && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Ferry</span>
                    <span className="font-medium text-[#0B2421]">SGD {ferry.totalAmount.toFixed(2)}</span>
                  </div>
                )}
                {transport && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Transport</span>
                    <span className="font-medium text-[#0B2421]">SGD {transport.totalAmount.toFixed(2)}</span>
                  </div>
                )}
                {accommodation && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Accommodation</span>
                    <span className="font-medium text-[#0B2421]">SGD {accommodation.totalAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold pt-3 border-t border-gray-100">
                  <span className="text-[#0B2421]">Total</span>
                  <span className="text-[#D4AF37]">SGD {totalAmount.toFixed(2)}</span>
                </div>
              </div>

              <div className="mb-6 p-4 border border-amber-200 bg-amber-50 rounded-xl">
                <p className="text-sm text-amber-700">
                  💡 Payment will be securely processed after confirmation
                </p>
              </div>

              <Button
                onClick={handlePayment}
                disabled={isProcessing}
                className="w-full h-14 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold text-lg rounded-xl disabled:opacity-50"
              >
                {isProcessing ? (
                  <span className="flex items-center gap-2">
                    <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing Payment...
                  </span>
                ) : (
                  `Pay SGD ${totalAmount.toFixed(2)}`
                )}
              </Button>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
